import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Style from './Governance.module.css';
import GridComponent from '../common/grid/GridComponent';
import SearchBox from '../common/searchBox/searchBox';
import ConfirmPopup from '../common/popover/confirmPopup';
import { DISPOSITION_BADGE } from './GovernanceShared';
import {
  listExtensionPolicies, getExtensionCounts, listExtensionPredefinedRules, listExtensionLookup,
  listExtensionEvaluation, deleteExtensionPolicy,
  setExtensionFilters, setExtensionPolicyFilters,
} from '../../store/entities/governance';

const DispositionBadge = ({ code }) => {
  const meta = DISPOSITION_BADGE[code];
  return meta
    ? <span className={`${Style.badge} ${Style[meta.cls]}`}>{meta.label}</span>
    : <span className={Style.dimCell}>—</span>;
};

// Who authored the row: Admin = a PixmAdmin attached a rule for this extension;
// System = the default catch-all, or an extension PIXM discovered in the fleet that
// nobody has governed yet. Lets an operator tell a deliberate policy from a row that
// is merely "seen, awaiting review" at a glance.
const SourceBadge = ({ source }) => (
  source === 'Admin'
    ? <span className={`${Style.badge} ${Style.low}`}>Admin</span>
    : <span className={`${Style.badge} ${Style.neutral}`}>System</span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Extension Controls — two blocks.
//
// 1. EXTENSION POLICY (PIXMC-6539). Every extension the customer has a stake in — the
//    default catch-all, each per-extension rule an admin attached, AND every extension
//    PIXM has discovered in the fleet but nobody has governed yet (source: System,
//    rule: null, status: UNSANCTIONED). Server-driven search / status-filter /
//    pagination via GridComponent, the same contract AppControls' UnsanctionedApps uses
//    — the list is a PAGE, so it must never be re-counted or re-sorted in the browser.
//    Edit/Remove per governed row open the per-extension editor
//    (ExtensionControlsEditor), where the whole draft → approve → publish lifecycle
//    lives. A discovered-but-catalogued row offers Attach Rule instead; an uncatalogued
//    one has no seeded rule to attach, so it only carries the marker.
//
//    The three stat tiles are the rule-aware totals of the WHOLE universe, from a
//    SEPARATE /counts endpoint — decoupled on purpose so filtering or paginating the
//    table can never move them (the old countBy() over the current page could, and did).
//
// 2. POLICY EVALUATION. What is actually installed. A CLIENT-SIDE JOIN of two reads:
//    the inventory lookup (Configuration DB — names, publisher, disposition) and the
//    aggregate (Customer DB — unique DEVICE count, last seen, folded status). They
//    cannot be joined in SQL because they live in different databases, so they are
//    matched here on logicalExtensionId. A lookup row with no aggregate match is
//    still rendered, with a blank count — dropping it would hide an extension that
//    is genuinely installed but has not produced a violation row yet.
//
// Deliberately absent, all confirmed out of scope: the logical extension ID column,
// a matched-rule column, native extension IDs, any permissions UI, a policy-level
// revision number, and the By extension / By user grouping toggle.
// ─────────────────────────────────────────────────────────────────────────────
const ExtensionControls = (props) => {
  const {
    customerId, policies, policyTotalCount, policyFilters, counts,
    predefinedRules, lookup, evaluation,
    policiesLoading, lookupLoading, evaluationLoading, filters,
  } = props;

  const [confirmDetach, setConfirmDetach] = useState(null);

  // The tiles and the attach catalogue are owned here. The POLICY LIST itself is owned
  // by the grid (mount-fetch + resetOnChange below), so it is deliberately NOT fetched
  // here — fetching it in both places would double-load and race on the first page.
  useEffect(() => {
    if (!customerId) return;
    props.getExtensionCounts(customerId);
    props.listExtensionPredefinedRules(customerId);
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Policy Evaluation (block 2) reads: refetched when ITS OWN filters change. The
  // disposition filter and search are applied server-side by the lookup (index-covered),
  // not by filtering in the browser.
  useEffect(() => {
    if (!customerId) return;
    props.listExtensionLookup(customerId, {
      search: filters.search || undefined,
      disposition: filters.disposition || undefined,
    });
    props.listExtensionEvaluation(customerId);
  }, [customerId, filters.search, filters.disposition]); // eslint-disable-line react-hooks/exhaustive-deps

  // The join. Keyed by logicalExtensionId; both sides are already scoped to this
  // customer by their own requests, so the id alone is a safe key here.
  const evaluationByIdentity = useMemo(() => {
    const map = new Map();
    (Array.isArray(evaluation) ? evaluation : []).forEach((e) => map.set(e.identity, e));
    return map;
  }, [evaluation]);

  const evaluationRows = useMemo(
    () => (Array.isArray(lookup) ? lookup : []).map((l) => {
      const agg = evaluationByIdentity.get(l.logicalExtensionId);
      return {
        ...l,
        // undefined, not 0: "we have no aggregate row for this" is a different fact
        // from "it is installed on zero devices", and the cell renders them
        // differently so a missing join is visible rather than looking like real data.
        installs: agg ? agg.installs : undefined,
        // INVENTORY FIRST, deliberately. The aggregate's MAX(OccurredAt) is only as fresh as
        // the last violation, and observations are now reported ONCE per extension per device
        // (then only on a rule change) -- so the aggregate's value stops advancing and means
        // "last newly installed", not "last seen". The inventory lookup keeps lastSeenOn current
        // through its own 7-day keep-alive, so it is the honest source for recency. The
        // aggregate remains the fallback for an extension inventory has not recorded yet.
        lastSeen: l.lastSeenOn || (agg && agg.lastSeen),
        // The lookup's dispositionCode is now the EFFECTIVE, rule-aware verdict — the lookup
        // query resolves the published, active per-extension rule itself (PIXMC-6538), so the
        // old client-side overlay that re-derived it from the policy list is gone. The aggregate
        // status remains the fallback for an extension the inventory has not recorded yet.
        status: l.dispositionCode || (agg && agg.status),
      };
    }),
    [lookup, evaluationByIdentity],
  );

  // Only rules not already attached can be attached — the same extension cannot hold two
  // policies, and the backend rejects a duplicate with a 409 anyway. Derived from the
  // predefined catalogue minus whatever the current page already shows attached; the
  // server is the real gate, so a stale page here only ever over-offers, never wrongly.
  const attachedRuleIds = new Set(
    (Array.isArray(policies) ? policies : []).filter((p) => p.rule).map((p) => p.rule.scanRuleId),
  );
  const attachableRules = (Array.isArray(predefinedRules) ? predefinedRules : [])
    .filter((r) => !attachedRuleIds.has(r.scanRuleId));

  // Removes the whole extension policy, not just the rule off a draft. Keyed on
  // policyId (not ruleId) so it works whatever state the policy is in -- the
  // rule-detach endpoint is DRAFT-only and made Remove fail outright on a published
  // extension. Removing governance changes the effective disposition shown in BOTH
  // tables and the tile totals, so all three are refreshed.
  const refetchPolicyTable = () => props.listExtensionPolicies(customerId, policyFilters);
  const handleDetachConfirmed = async () => {
    const target = confirmDetach;
    setConfirmDetach(null);
    if (!target || !target.policyId) return;
    const res = await props.deleteExtensionPolicy(customerId, target.policyId);
    if (res !== undefined) {
      refetchPolicyTable();
      props.getExtensionCounts(customerId);
      props.listExtensionLookup(customerId, {
        search: filters.search || undefined,
        disposition: filters.disposition || undefined,
      });
    }
  };

  // A DISABLED rule is not merely a UI state: the heartbeat resolver filters rules on
  // `r.isActive = 1`, so a disabled rule is dropped from the payload entirely and its policy
  // reaches the device with an empty rls[]. Nothing enforces, nothing reports. A row with no
  // rule at all (a discovered, ungoverned extension) has nothing to enable -- shown as —.
  const enabledCell = (p) => {
    if (!p.rule) return <span className={Style.dimCell}>—</span>;
    return p.rule.isActive
      ? <span className={`${Style.badge} ${Style.low}`}>Enabled</span>
      : (
        <span
          className={`${Style.badge} ${Style.neutral}`}
          title="This rule is disabled, so it is not sent to managed browsers and nothing is enforced for it. Open Edit to re-enable it."
        >
          Disabled
        </span>
      );
  };

  const versionCell = (p) => {
    if (!p.rule) return <span className={Style.dimCell}>—</span>;
    return (
      <>
        {p.publishedVersionNumber
          ? <span className={`${Style.badge} ${Style.low}`}>v{p.publishedVersionNumber}</span>
          : <span className={`${Style.badge} ${Style.neutral}`}>Not published</span>}
        {p.openStatus && (
          <span className={Style.draftStateInline}>({p.openStatus} v{p.openVersionNumber})</span>
        )}
      </>
    );
  };

  const reasonCell = (p) => {
    if (p.rule && p.rule.isDefaultRule) {
      return <span className={Style.dcSummaryDefault}>Applies to every customer</span>;
    }
    if (p.rule && p.rule.decisionReason) {
      // title carries the untruncated reason — .truncCell clips to one line.
      return <span className={Style.truncCell} title={p.rule.decisionReason}>{p.rule.decisionReason}</span>;
    }
    // A discovered, ungoverned extension: no rule, so no reason — say why rather than —.
    if (!p.rule) return <span className={Style.dcSummaryDefault}>Discovered — not yet governed</span>;
    return <span className={Style.dcSummaryDefault}>None</span>;
  };

  const extensionCell = (p) => {
    const isDefault = !!(p.rule && p.rule.isDefaultRule);
    return (
      <span className={Style.domCell}>
        {isDefault
          ? 'Any other extension'
          : (p.extensionName || p.logicalExtensionId || '—')}
        {p.isSurrogateId && (
          // Explains why this row cannot be governed: an uncatalogued extension has no
          // seeded rule to attach until PIXM adds one.
          <span
            className={`${Style.badge} ${Style.neutral}`}
            style={{ marginLeft: 8 }}
            title="Not in the PIXM extension catalogue, so no predefined rule can be attached to it yet"
          >
            Uncatalogued
          </span>
        )}
      </span>
    );
  };

  const actionCell = (p) => {
    const isDefault = !!(p.rule && p.rule.isDefaultRule);
    if (p.rule) {
      return (
        <div className={Style.rowActions}>
          {/* Edit opens the per-extension editor, where the whole draft → approve →
              publish lifecycle lives — the same shape Web/AI use. Nothing about the
              version is actioned from this landing table. */}
          <button className={Style.btn} onClick={() => props.onEditRule && props.onEditRule(p)}>Edit</button>
          {isDefault
            // Locked in the UI as a signal only. The real gate is server-side: the proc
            // refuses to detach the default rule for anyone but a PixmAdmin, because
            // switching it off ends all extension reporting for the customer.
            ? <span className={Style.dimCell} title="Only a PixmAdmin can remove the default rule">Locked</span>
            : <button className={Style.btn} onClick={() => setConfirmDetach(p)}>Remove</button>}
        </div>
      );
    }
    // Discovered, ungoverned. Catalogued → can be governed (Attach opens the editor in
    // attach mode). Uncatalogued → nothing to attach yet, so no action.
    if (p.isSurrogateId) return <span className={Style.dimCell}>—</span>;
    return (
      <div className={Style.rowActions}>
        <button
          className={Style.btn}
          title="Attach a predefined rule to govern this extension"
          onClick={() => props.onAddRule && props.onAddRule()}
        >
          Attach Rule
        </button>
      </div>
    );
  };

  const gridDef = {
    name: 'extensionPolicyGrid',
    columns: [
      { fieldName: 'extension', displayName: 'Extension', width: '20%', noTooltip: true },
      { fieldName: 'rule', displayName: 'Rule', width: '15%', noTooltip: true },
      { fieldName: 'policy', displayName: 'Policy', width: '11%', noTooltip: true },
      { fieldName: 'enabled', displayName: 'Enabled', width: '10%', noTooltip: true },
      { fieldName: 'version', displayName: 'Version', width: '13%', noTooltip: true },
      { fieldName: 'source', displayName: 'Source', width: '9%', noTooltip: true },
      { fieldName: 'reason', displayName: 'Reason', width: '12%', noTooltip: true },
      { fieldName: 'action', displayName: 'Actions', width: '10%', noTooltip: true },
    ],
  };

  const gridRecords = (Array.isArray(policies) ? policies : []).map((p) => ({
    ...p,
    extension: extensionCell(p),
    rule: (p.rule && p.rule.scanRuleName) || <span className={Style.dimCell}>—</span>,
    // The POLICY column is the row's effective, rule-aware disposition — the same number
    // the tiles count — so a filtered table and its tiles always tell the same story.
    policy: <DispositionBadge code={p.status} />,
    enabled: enabledCell(p),
    version: versionCell(p),
    source: <SourceBadge source={p.source} />,
    reason: reasonCell(p),
    action: actionCell(p),
  }));

  const getPageData = (pageNo, pageSize) => props.listExtensionPolicies(
    customerId, { ...policyFilters, page: pageNo, pageSize },
  );

  // See UnsanctionedApps.jsx: the grid's mount-fetch guard needs `params` to already
  // match the loaded customer, and a filter change must reset to page 1 and refetch.
  const resetOnChange = [customerId, policyFilters.search, policyFilters.disposition].join('|');

  const from = policyTotalCount === 0 ? 0 : (policyFilters.page - 1) * policyFilters.pageSize + 1;
  const to = Math.min(policyFilters.page * policyFilters.pageSize, policyTotalCount);

  return (
    <div style={{ width: '100%' }}>
      <div className={Style.statRow3}>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Sanctioned Extensions</div>
          <div className={Style.statValue}>{counts.sanctioned}</div>
          <div className={Style.statNote}>Approved and left in place</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Unsanctioned Extensions</div>
          <div className={Style.statValue}>{counts.unsanctioned}</div>
          <div className={Style.statNote}>Found in the fleet, not yet reviewed</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Denied Extensions</div>
          <div className={Style.statValue}>{counts.denied}</div>
          <div className={Style.statNote}>Disabled on every managed browser</div>
        </div>
      </div>

      {/* ── Block 1: Extension Policy ─────────────────────────────────────── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>Extension Policy</div>
          <div className={Style.cardSubtitle}>
            Every governed extension and every one discovered across the fleet. Rules are predefined
            and cannot be edited here — attach one to govern an extension. Every matching rule is
            evaluated independently, so rules have no ordering.
          </div>
        </div>
        <div className={Style.toolbar}>
          <SearchBox
            width="280px"
            placeholder="Search extensions..."
            value={policyFilters.search}
            handleSearch={(text) => props.setExtensionPolicyFilters({ search: text, page: 1 })}
          />
          <select
            className={Style.sortSelect}
            value={policyFilters.disposition || ''}
            onChange={(e) => props.setExtensionPolicyFilters({ disposition: e.target.value, page: 1 })}
          >
            <option value="">All statuses</option>
            <option value="DENIED">Denied</option>
            <option value="SANCTIONED">Sanctioned</option>
            <option value="UNSANCTIONED">Unsanctioned</option>
          </select>
          <div className={Style.toolbarSpacer} />
          <div className={Style.resultCount}>
            {policyTotalCount > 0 ? `Showing ${from}–${to} of ${policyTotalCount}` : 'No extensions'}
          </div>
        </div>
        <div className={Style.cardBody}>
          <GridComponent
            loaderFlag={policiesLoading}
            resetOnChange={resetOnChange}
            data={gridRecords}
            totalRecords={policyTotalCount}
            pageSize={policyFilters.pageSize}
            definition={gridDef}
            getPrevPageData={getPageData}
            getNextPageData={getPageData}
            rowClick={() => {}}
            params={customerId}
          />
        </div>
        <div className={Style.cardFooter}>
          <button
            className={`${Style.btn} ${Style.btnPrimary}`}
            disabled={attachableRules.length === 0}
            title={attachableRules.length === 0 ? 'Every predefined rule is already attached' : ''}
            // Opens the editor in attach mode (no ?policyId=), the same way Web/AI's
            // Add Site does. Picking the rule, then approving and publishing it, all
            // happen on that one page rather than in a modal here.
            onClick={() => props.onAddRule && props.onAddRule()}
          >
            Attach Rule
          </button>
        </div>
      </div>

      {/* ── Block 2: Policy Evaluation ────────────────────────────────────── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>Policy Evaluation</div>
          <div className={Style.cardSubtitle}>
            Every extension found across the fleet. A catalogued extension gets one row covering all
            browsers; an uncatalogued one gets one row per browser. Installs counts unique devices.
          </div>
        </div>

        <div className={Style.cardBody}>
          <div className={Style.evalFilterBar}>
            <SearchBox
              width="280px"
              placeholder="Search extensions..."
              value={filters.search}
              handleSearch={(text) => props.setExtensionFilters({ search: text })}
            />
            <select
              className={Style.sortSelect}
              value={filters.disposition || ''}
              onChange={(e) => props.setExtensionFilters({ disposition: e.target.value })}
            >
              <option value="">All statuses</option>
              <option value="DENIED">Denied</option>
              <option value="SANCTIONED">Sanctioned</option>
              <option value="UNSANCTIONED">Unsanctioned</option>
            </select>
          </div>

          <table className={Style.table}>
            <thead>
              <tr>
                <th>Extension</th>
                <th>Installs</th>
                <th>Last Seen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!lookupLoading && evaluationRows.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>
                    No extensions reported yet
                  </td>
                </tr>
              )}
              {evaluationRows.map((r) => (
                <tr key={r.logicalExtensionId}>
                  <td className={Style.domCell}>
                    {r.extensionName || r.logicalExtensionId}
                    {r.isSurrogateId && (
                      // Explains why this row cannot be governed: an uncatalogued
                      // extension has no seeded rule to attach until PIXM adds one.
                      <span
                        className={`${Style.badge} ${Style.neutral}`}
                        style={{ marginLeft: 8 }}
                        title="Not in the PIXM extension catalogue, so no predefined rule can be attached to it yet"
                      >
                        Uncatalogued
                      </span>
                    )}
                  </td>
                  <td>
                    {r.installs === undefined
                      ? <span className={Style.dimCell} title="No violation activity recorded for this extension yet">—</span>
                      : r.installs}
                  </td>
                  <td>
                    {r.lastSeen
                      ? new Date(r.lastSeen).toLocaleString()
                      : <span className={Style.dimCell}>—</span>}
                  </td>
                  <td><DispositionBadge code={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {evaluationLoading && <div className={Style.statNote}>Refreshing device counts…</div>}
        </div>
      </div>

      {confirmDetach && (
        <ConfirmPopup
          details={{
            icon: '', type: 'warning', title: 'Remove Rule?',
            description: `Remove "${(confirmDetach.rule && confirmDetach.rule.scanRuleName) || ''}" from `
              + `${(confirmDetach.rule && (confirmDetach.rule.extensionName || confirmDetach.rule.logicalExtensionId)) || 'this extension'}? `
              + 'This deletes the extension’s policy and any open draft. The rule definition itself is unaffected.',
            buttons: 'Y,N',
          }}
          onYesClick={handleDetachConfirmed}
          onNoClick={() => setConfirmDetach(null)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  policies: state.entities.governance.extensionPolicies,
  policyTotalCount: state.entities.governance.extensionPolicyTotalCount,
  policyFilters: state.entities.governance.extensionPolicyFilters,
  policiesLoading: state.entities.governance.extensionPoliciesLoading,
  counts: state.entities.governance.extensionCounts,
  predefinedRules: state.entities.governance.extensionPredefinedRules,
  lookup: state.entities.governance.extensionLookup,
  lookupLoading: state.entities.governance.extensionLookupLoading,
  evaluation: state.entities.governance.extensionEvaluation,
  evaluationLoading: state.entities.governance.extensionEvaluationLoading,
  filters: state.entities.governance.extensionFilters,
});

const mapDispatchToProps = (dispatch) => ({
  listExtensionPolicies: (customerId, filters) => Promise.resolve(dispatch(listExtensionPolicies(customerId, filters))),
  getExtensionCounts: (customerId) => Promise.resolve(dispatch(getExtensionCounts(customerId))),
  listExtensionPredefinedRules: (customerId) => Promise.resolve(dispatch(listExtensionPredefinedRules(customerId))),
  listExtensionLookup: (customerId, filters) => Promise.resolve(dispatch(listExtensionLookup(customerId, filters))),
  listExtensionEvaluation: (customerId, filters) => Promise.resolve(dispatch(listExtensionEvaluation(customerId, filters))),
  deleteExtensionPolicy: (customerId, policyId) => Promise.resolve(dispatch(deleteExtensionPolicy(customerId, policyId))),
  setExtensionFilters: (patch) => dispatch(setExtensionFilters(patch)),
  setExtensionPolicyFilters: (patch) => dispatch(setExtensionPolicyFilters(patch)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExtensionControls);
